import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Logo from '../components/Logo';
import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

const CowInvestmentDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [cowData, setCowData] = useState(null);

  useEffect(() => {
    const fetchProdukDetail = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching produk detail untuk id:', id);
        
        // Fetch produk dari API /api/produk/public/{id}
        const response = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_DETAIL(id));
        
        // Handle struktur response dari API
        let produk = null;
        if (response.data && response.data.id) {
          produk = response.data;
        } else if (response.data && response.data.produk) {
          produk = response.data.produk;
        } else if (response.id) {
          produk = response;
        }
        
        if (!produk) {
          throw new Error('Data produk tidak ditemukan');
        }

        // Format tanggal dari created_at
        const formatDate = (dateString) => {
          if (!dateString) return '-';
          try {
            const date = new Date(dateString);
            const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 
                           'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
            const year = date.getFullYear();
            const month = months[date.getMonth()];
            const day = date.getDate();
            return `${year} / ${day} ${month} ${year}`;
          } catch (error) {
            return dateString;
          }
        };

        // Format currency
        const formatCurrency = (value) => {
          if (!value && value !== 0) return '-';
          return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
          }).format(value);
        };

        // Tentukan status berdasarkan is_available dan is_sold
        let status = 'Belum Terjual';
        if (produk.is_sold) {
          status = 'Terjual';
        } else if (!produk.is_available) {
          status = 'Booked';
        }

        // Fetch images untuk produk (optional)
        let imageUrl = '/sapi1.jpg';
        try {
          const imagesResponse = await apiClient.get(API_ENDPOINTS.PRODUK.PUBLIC_IMAGES(id));
          let images = [];
          
          if (imagesResponse.data && imagesResponse.data.images) {
            images = imagesResponse.data.images;
          } else if (imagesResponse.images) {
            images = imagesResponse.images;
          } else if (Array.isArray(imagesResponse.data)) {
            images = imagesResponse.data;
          } else if (Array.isArray(imagesResponse)) {
            images = imagesResponse;
          }
          
          if (images.length > 0) {
            imageUrl = images[0].url || images[0] || '/sapi1.jpg';
          }
        } catch (error) {
          console.error('Error fetching images:', error);
          // Gunakan default image
        }

        // Hitung profit bersama dan profit berdasarkan percent_profit
        const hargaBeli = produk.harga_beli || 0;
        const hargaJual = produk.harga_jual || 0;
        const feeMarketing = produk.fee_marketing || 0;
        const percentProfit = produk.percent_profit || 0; // Ambil dari API
        const profitBersama = hargaJual > 0 ? hargaJual - hargaBeli - feeMarketing : 0;
        // Profit percent = profit_bersama * (percent_profit / 100)
        const profitPercent = profitBersama > 0 && percentProfit > 0 ? profitBersama * (percentProfit / 100) : 0;
        // Total keuntungan = profit_bersama - profit_percent
        const totalKeuntungan = profitBersama - profitPercent;

        // Transform data produk ke format yang sesuai dengan UI
        setCowData({
          id: produk.id || id,
          kode: produk.id || id,
          nama: produk.nama || 'Produk',
          tanggal: formatDate(produk.created_at),
          jenisProduk: produk.jenis_produk_detail_nama || produk.jenis_produk_nama || '-',
          targetBobot: produk.target_bobot ? `${produk.target_bobot} kg` : '-',
          kategori: produk.kategori_produk_nama || '-',
          hargaBeli: formatCurrency(produk.harga_beli),
          hargaJual: formatCurrency(produk.harga_jual),
          feeMarketing: formatCurrency(produk.fee_marketing),
          profitBersama: formatCurrency(profitBersama),
          profitPercent: formatCurrency(profitPercent),
          percentProfit: percentProfit, // Simpan untuk ditampilkan
          totalKeuntungan: formatCurrency(totalKeuntungan),
          status: status,
          image: imageUrl,
          farm: produk.kandang_nama || produk.mitra_nama || 'pakMoo'
        });
      } catch (error) {
        console.error('Error fetching produk detail:', error);
        // Set error state atau redirect
        setCowData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProdukDetail();
  }, [id]);

  const handleBack = () => {
    navigate('/daftar-ternak');
  };

  if (loading || !cowData) {
    return (
      <section className="min-h-screen bg-white">
        <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
          <div className="flex items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-white">
      <div className="max-w-lg mx-auto bg-white shadow-2xl min-h-screen">
        
        {/* Header */}
        <header className="px-4 pt-8 pb-4 bg-white border-b border-gray-100">
          <div className="flex items-center space-x-3 mb-4">
            <button 
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="fa-solid fa-arrow-left text-primary text-xl"></i>
            </button>
            <div className="flex items-center space-x-2">
              <Logo size="small" showText={false} />
              <span className="font-bold text-primary">PAK MoO</span>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pb-24 bg-white">
          {/* Hero Image Section */}
          <section className="relative h-64 overflow-hidden">
            <div 
              className="absolute inset-0 bg-cover bg-center blur-sm"
              style={{
                backgroundImage: `url(${cowData.image})`,
                filter: 'blur(4px)'
              }}
            >
            </div>
            <div className="absolute inset-0 bg-primary/60"></div>
            
            {/* Tags di corners */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg">
              <span className="text-sm font-semibold text-secondary">{cowData.kode}</span>
            </div>
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg">
              <span className="text-sm font-semibold text-secondary">{cowData.nama}</span>
            </div>

            {/* Hashtags di bottom-left */}
            <div className="absolute bottom-4 left-4 bg-primary/80 backdrop-blur-sm px-3 py-2 rounded-lg">
              <p className="text-white text-xs font-medium">#IngatQurban</p>
              <p className="text-white text-xs font-medium">#IngatPakMoo</p>
            </div>

            {/* Farm info di bottom-right */}
            <div className="absolute bottom-4 right-4 bg-primary/80 backdrop-blur-sm px-3 py-2 rounded-lg text-right">
              <p className="text-white text-xs font-medium">pakMoo</p>
              <p className="text-white text-xs font-medium">{cowData.farm}</p>
            </div>
          </section>

          {/* Title Section */}
          <section className="px-4 mt-4">
            <h1 className="text-3xl font-bold text-secondary mb-2">{cowData.nama}</h1>
            <p className="text-gray-600 text-sm">
              {cowData.kode} {cowData.tanggal}
            </p>
          </section>

          {/* Detail List Section */}
          <section className="px-4 mt-6">
            <div className="bg-white rounded-2xl shadow-lg p-4 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Jenis Produk</span>
                <span className="text-secondary font-semibold">{cowData.jenisProduk}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Target Bobot</span>
                <span className="text-secondary font-semibold">{cowData.targetBobot}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Kategori</span>
                <span className="text-secondary font-semibold">{cowData.kategori}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Harga Beli</span>
                <span className="text-secondary font-semibold">{cowData.hargaBeli}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Harga Jual</span>
                <span className="text-secondary font-semibold">{cowData.hargaJual}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Fee Marketing</span>
                <span className="text-secondary font-semibold">{cowData.feeMarketing}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Profit Bersama</span>
                <span className="text-secondary font-semibold">{cowData.profitBersama}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600 font-medium">Profit {cowData.percentProfit || 0}%</span>
                <span className="text-secondary font-semibold">{cowData.profitPercent}</span>
              </div>
              
              <div className="flex justify-between items-center py-2 bg-accent/10 -mx-4 px-4 py-3 rounded-lg">
                <span className="text-gray-700 font-bold">Total Keuntungan</span>
                <span className="text-primary font-bold text-lg">{cowData.totalKeuntungan}</span>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <section className="px-4 mt-6 space-y-3">
            <button 
              className="w-full bg-gray-200 text-gray-700 py-4 rounded-xl font-semibold text-sm disabled cursor-not-allowed"
              disabled
            >
              {cowData.status}
            </button>
          </section>
        </main>
      </div>
    </section>
  );
};

export default CowInvestmentDetailPage;

